import type { Action, Resource, Role } from '../model/types';
import { action, resource, role } from '../model/types';

type RouteRule = {
  roles?: Role[];
  permission?: { resource: Resource; action: Action };
};

const routePermissions: Record<string, RouteRule> = {
  '/crm': { permission: { resource: resource.crm, action: action.read } },
  // Layout templates, moved out of the admin-only /system-configuration into the
  // CRM tab row. This more-specific entry overrides the `crm:read` rule above:
  // `operation` holds crm:read but NO company_settings grants, so it would land
  // on a page whose every write 403s. Consulting holds company_settings
  // create/update (delete stays admin-only — the button is hidden without it).
  '/crm/layouts': { roles: [role.admin, role.consulting] },
  '/dashboard': {
    permission: { resource: resource.dashboard, action: action.read },
  },
  '/itineraries': {
    permission: { resource: resource.itineraries, action: action.read },
  },
  // Unlisted sold-history builder. Same gate as the itineraries list;
  // not under `/itineraries/` so it needs its own exact key.
  '/itinerary-builder-eval': {
    permission: { resource: resource.itineraries, action: action.read },
  },
  '/products': {
    permission: { resource: resource.products, action: action.read },
  },
  '/operations': {
    permission: { resource: resource.operations, action: action.read },
  },
  // Incident management page — operations/admin manage, finance views + exports.
  // The API enforces the finer incidents:*:any grants; this is a coarse gate.
  '/incidents-management': {
    roles: [role.admin, role.operation, role.finance],
  },
  // `system` is admitted so the cross-organisation super-user can triage
  // support tickets across every tenant without switching organization; the
  // API decides the org scope per caller (support.router's manageOrgScope).
  '/support-management': { roles: [role.admin, role.operation, role.system] },
  '/recommendation': {
    permission: { resource: resource.recommendation, action: action.read },
  },
  '/moodboard': {
    permission: { resource: resource.dashboard, action: action.read },
  },
  '/inbox': { permission: { resource: resource.inbox, action: action.read } },
  // Marketing HUB (XT-808): deliberately no rule. Anyone with a hub role —
  // platform `marketing_admin` or a marketing.access_role row — may enter, so
  // the hub gates itself (`MarketingAccessGate` via `hub.me`; every procedure
  // via `hubProcedure`).

  '/cms/products': {
    permission: { resource: resource.products, action: action.read },
  },
  '/cms/itineraries': {
    permission: { resource: resource.itineraries, action: action.read },
  },
  '/cms/pages': {
    permission: { resource: resource.companySettings, action: action.read },
  },
  '/cms/taxonomy': {
    permission: { resource: resource.companySettings, action: action.read },
  },
  '/sandbox/agent-panel': {
    permission: { resource: resource.agentPanel, action: action.use },
  },
  '/api-monitoring': { roles: [role.admin] },
  '/auto-translate-catalog': { roles: [role.admin] },
  '/custom-regions': { roles: [role.admin] },
  '/export-configuration': { roles: [role.admin] },
  '/inconsistency-resolver': { roles: [role.admin] },
  '/management': { roles: [role.admin] },
  // Bulk Status Change admits the cross-org super-user as well as admin; this
  // more-specific entry overrides the admin-only `/management` rule. The API
  // enforces the same pair (`roleRequired(Admin, System)`).
  '/management/bulk-status-change': { roles: [role.admin, role.system] },
  // Payment Logs is the finance team's surface, not an admin tool — legacy v2
  // gated it `[ADMIN, FINANCE, SUPER_FINANCE]` and the v3 port narrowed it to
  // admin-only by inheriting the `/management` prefix rule (XT-620). This
  // more-specific entry restores the legacy trio. Writes stay separately
  // gated: the table's Edit / Mark-as-paid actions check `itineraries:update`,
  // which finance does not hold, so finance lands on a read-only table.
  '/management/payment-logs': {
    roles: [role.admin, role.finance, role.superFinance],
  },
  '/messaging': { roles: [role.admin] },
  '/pricing-configuration': { roles: [role.admin] },
  // Pricing Type is the finance team's other legacy surface. v2 put it and
  // Payment Logs in the same avatar-menu "Pricing Settings" group and gated
  // both at [ADMIN, FINANCE, SUPER_FINANCE]
  // (travel_planner_front/config/route-permissions.ts); the v3 port narrowed
  // both to admin-only (XT-620). Scoped to this ONE tab on purpose — the
  // sibling tabs (default-pricing, vat, exchange-rate) were never granted to
  // finance in v2 and stay admin-only via the prefix rule above.
  '/pricing-configuration/pricing-type': {
    roles: [role.admin, role.finance, role.superFinance],
  },
  '/system-configuration': { roles: [role.admin] },
  // Partner Configuration (Eleggia DMC profile) is its own top-level page,
  // reachable by the Eleggia DMC roles (not just admin) — gated by the
  // `dmc_profile:read` permission so the 5 roles + admin qualify.
  '/partner-configuration': {
    permission: { resource: resource.dmcProfile, action: action.read },
  },
  '/tasks-management': { roles: [role.admin] },
  '/user-settings': { roles: [role.admin] },
};

export const getRoutePermission = (pathname: string): RouteRule | undefined => {
  if (/^\/products\/[^/]+\/edit(?:\/|$)/.test(pathname)) {
    return {
      permission: { resource: resource.products, action: action.update },
    };
  }

  const exact = routePermissions[pathname];
  if (exact) return exact;

  const prefix = Object.keys(routePermissions)
    .filter((key) => pathname.startsWith(`${key}/`))
    .sort((a, b) => b.length - a.length)
    .at(0);

  return prefix ? routePermissions[prefix] : undefined;
};
